/**
 * SSE (Server-Sent Events) Service
 * Глобальный сервис для работы с SSE соединением
 */

class SSEService {
    constructor() {
        this.eventSource = null;
        this.status = 'disconnected'; // 'connecting', 'connected', 'error', 'disconnected'
        // Формируем URL с учетом BASE_URL (как в router.js)
        // axios автоматически добавляет /api через baseURL, но EventSource работает напрямую
        // Поэтому нужно вручную добавить /api в путь
        const baseURL = process.env.BASE_URL || '';
        // Убираем trailing slash из baseURL и формируем полный путь
        const normalizedBase = baseURL.replace(/\/$/, '');
        // Формируем URL: baseURL + /api/v2/events/stream
        // Если baseURL пустой, получится /api/v2/events/stream
        // Если baseURL = /app, получится /app/api/v2/events/stream
        this.url = `${normalizedBase}/api/v2/events/stream`;
        this.subscribers = new Map(); // Map<eventName, Set<callback>>
        this.customEvents = new Set(); // Кастомные события, кроме стандартного message
        this.eventHandlers = new Map(); // Map<eventName, handler> для EventSource
        this.lastEventData = new Map(); // Map<eventName, any> — кэш последнего события
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 3000; // 3 секунды
        this.reconnectTimer = null;
        this.shouldReconnect = true;
    }

    /**
     * Подключение к SSE серверу
     */
    connect() {
        if (this.eventSource) {
            this.disconnect();
        }

        // Проверяем, что пользователь авторизован
        if (!localStorage.LoggedIn) {
            console.warn('SSE: Пользователь не авторизован, подключение отменено');
            return;
        }

        this.status = 'connecting';
        this.shouldReconnect = true;

        try {
            this.eventSource = new EventSource(this.url, {
                withCredentials: true,
            });

            // Новый EventSource — сбрасываем обработчики
            this.eventHandlers.clear();

            this.eventSource.onopen = () => {
                this.status = 'connected';
                this.reconnectAttempts = 0;

                // Навешиваем все известные кастомные события
                this.customEvents.forEach((eventName) => {
                    this.registerEventListener(eventName);
                });
            };

            this.eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    this.notifySubscribers('message', data);
                } catch (error) {
                    console.error('SSE: Ошибка парсинга сообщения:', error);
                }
            };

            this.eventSource.onerror = (err) => {
                console.error('❌ SSE error:', err);

                if (this.eventSource?.readyState === EventSource.CLOSED) {
                    console.warn('SSE connection closed');
                    this.status = 'error';

                    // Переподключение при ошибке
                    if (this.shouldReconnect
                        && this.reconnectAttempts < this.maxReconnectAttempts
                    ) {
                        this.scheduleReconnect();
                    } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                        console.error(
                            'SSE: Достигнуто максимальное количество попыток переподключения',
                        );
                        this.status = 'disconnected';
                    }
                }
            };
        } catch (error) {
            console.error('SSE: Ошибка создания соединения:', error);
            this.status = 'error';
        }
    }

    /**
     * Отключение от SSE сервера
     */
    disconnect() {
        this.shouldReconnect = false;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
            this.status = 'disconnected';
        }

        // Обработчики нужно навесить заново на новый EventSource
        this.eventHandlers.clear();
    }

    /**
     * Переподключение к SSE серверу
     */
    reconnect() {
        this.disconnect();
        this.reconnectAttempts = 0;
        this.connect();
    }

    /**
     * Планирование переподключения
     */
    scheduleReconnect() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
        }

        this.reconnectAttempts += 1;
        const delay = this.reconnectDelay * this.reconnectAttempts; // Экспоненциальная задержка

        console.warn(`[SSE]: Переподключение через ${delay}ms (попытка ${this.reconnectAttempts})`);

        this.reconnectTimer = setTimeout(() => {
            if (this.shouldReconnect && localStorage.LoggedIn) {
                this.connect();
            }
        }, delay);
    }

    /**
     * Подписка на событие
     * @param {string} eventName - Название события ('message', 'notification' или кастомное)
     * @param {Function} callback - Функция-обработчик события
     * @returns {Function} Функция для отписки
     */
    subscribe(eventName, callback) {
        if (typeof callback !== 'function') {
            console.error('SSE: callback должен быть функцией');
            return () => {};
        }

        if (!this.subscribers.has(eventName)) {
            this.subscribers.set(eventName, new Set());
        }

        this.subscribers.get(eventName).add(callback);

        // Регистрируем кастомное событие на EventSource
        if (eventName !== 'message') {
            this.customEvents.add(eventName);
            if (this.eventSource && this.status === 'connected') {
                this.registerEventListener(eventName);
            }
        }

        // Если соединение не установлено и пользователь авторизован, подключаемся
        if (this.status === 'disconnected' && localStorage.LoggedIn) {
            this.connect();
        }

        // Возвращаем последнее значение события, если оно уже приходило
        if (this.lastEventData.has(eventName)) {
            const cached = this.lastEventData.get(eventName);
            // Вызываем асинхронно, чтобы не блокировать подписку
            setTimeout(() => {
                try {
                    callback(cached);
                } catch (error) {
                    console.error(`SSE: Ошибка при отдаче кэша для ${eventName}:`, error);
                }
            }, 0);
        }

        // Возвращаем функцию для отписки
        return () => {
            this.unsubscribe(eventName, callback);
        };
    }

    /**
     * Отписка от события
     * @param {string} eventName - Название события
     * @param {Function} callback - Функция-обработчик события
     */
    unsubscribe(eventName, callback) {
        if (this.subscribers.has(eventName)) {
            this.subscribers.get(eventName).delete(callback);

            // Если больше нет подписчиков, можно отключиться (опционально)
            if (this.subscribers.get(eventName).size === 0) {
                this.subscribers.delete(eventName);
            }
        }
    }

    /**
     * Уведомление подписчиков о событии
     * @param {string} eventName - Название события
     * @param {*} data - Данные события
     */
    notifySubscribers(eventName, data) {
        this.lastEventData.set(eventName, data);
        if (this.subscribers.has(eventName)) {
            this.subscribers.get(eventName).forEach((callback) => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`SSE: Ошибка в callback для события ${eventName}:`, error);
                }
            });
        }
    }

    /**
     * Получить текущий статус соединения
     * @returns {string} Статус соединения
     */
    getStatus() {
        return this.status;
    }

    /**
     * Регистрация обработчика для кастомного события EventSource
     * @param {string} eventName
     */
    registerEventListener(eventName) {
        if (!this.eventSource) {
            return;
        }

        if (this.eventHandlers.has(eventName)) {
            return;
        }

        const handler = (event) => {
            try {
                const data = JSON.parse(event.data);
                this.notifySubscribers(eventName, data);
            } catch (error) {
                console.error(`SSE: Ошибка парсинга ${eventName}:`, error);
            }
        };

        this.eventHandlers.set(eventName, handler);
        this.eventSource.addEventListener(eventName, handler);
    }
}

// Создаем единственный экземпляр сервиса (Singleton)
const sseService = new SSEService();

export default sseService;
